import { StudioRoute, useGetRoute, useUpdateRoute } from '@/lib/react-query'
import { toastSuccess } from '@/utils/toast'
import { FormikConfig } from 'formik'
import { yup } from '@knots/shared-frontend'
import cronstrue from 'cronstrue'

export type RouteSettingsSchemaType = Pick<
  StudioRoute,
  'runtime' | 'type' | 'schedule' | 'scheduleTimeZone' | 'loggingEnabled' | 'authEnabled'
>

export const RouteSettingsSchema = yup.object().shape({
  runtime: yup.string().required(),
  type: yup.string().required(),
  schedule: yup
    .string()
    .test('schedule-test', 'Should be a valid schedule crontab config', (value, validationContext) => {
      try {
        const { parent } = validationContext
        if (parent.type !== 'cron' || !value) return true
        cronstrue.toString(value!)
        return true
      } catch {
        return false
      }
    }),
  scheduleTimeZone: yup.string().required(),
  loggingEnabled: yup.boolean().required(),
  authEnabled: yup.boolean().required(),
})

export const useForm = (
  projectId: number,
  routeId: number,
  close: () => void
): FormikConfig<RouteSettingsSchemaType> => {
  const { data } = useGetRoute(projectId, routeId)
  const route = data!.route

  const mutation = useUpdateRoute(projectId, routeId)

  const initialValues: RouteSettingsSchemaType = {
    runtime: route.runtime,
    type: route.type,
    schedule: route.schedule ?? '5 0 * * 1',
    scheduleTimeZone: route.scheduleTimeZone,
    loggingEnabled: route.loggingEnabled,
    authEnabled: route.authEnabled,
  }

  const onSubmit = async (values: RouteSettingsSchemaType) => {
    const clearedValues: RouteSettingsSchemaType = {
      runtime: values.runtime,
      type: values.type,
      schedule: values.schedule ?? undefined,
      scheduleTimeZone: values.scheduleTimeZone ?? undefined,
      loggingEnabled: values.loggingEnabled ?? false,
      authEnabled: values.authEnabled ?? false,
    }

    await mutation.mutateAsync({ route: clearedValues })
    close()
    toastSuccess('Successfully updated the route')
  }

  return {
    initialValues,
    onSubmit,
    validationSchema: RouteSettingsSchema,
  }
}
